import axios, { type AxiosInstance } from 'axios';

declare module 'axios' {
  export interface AxiosRequestConfig {
    /**
     * Opts a request out of the global "401 -> clear session and redirect to
     * /login" handling below. Use this for calls where a 401 is an expected,
     * recoverable outcome that the caller handles inline (e.g. a wrong
     * current password on the change-password form) rather than a sign that
     * the session itself is invalid — without this, that request would log
     * the user out and redirect them away before they ever saw the error.
     */
    skipAuthRedirect?: boolean;
  }
}

const TOKEN_KEY = 'zula.token';

export function loadToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function saveToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export function createApi(): AxiosInstance {
  const api = axios.create({ baseURL: '/api' });
  api.interceptors.request.use((config) => {
    const token = loadToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });
  api.interceptors.response.use(
    (r) => r,
    (error) => {
      if (error?.response?.status === 401 && !error?.config?.skipAuthRedirect) {
        clearToken();
        if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
          window.location.assign('/login');
        }
      }
      return Promise.reject(error);
    },
  );
  return api;
}

export const api = createApi();
